import { useAuth0 } from "@auth0/auth0-react";
import {
  Client,
  SessionManager,
  type SessionV2,
  UserToken,
} from "@decentriq/core";
import { attestation as attestationProto } from "@decentriq/proto";
import * as forge from "node-forge";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "components";
import {
  type ApiCoreContextValue,
  ApiCoreProvider,
} from "contexts/apicore/apicore";
import { useConfiguration } from "contexts/configuration/configuration";
import { clearDecentriqStorage, logInfo } from "utils";
import { type EnclaveToken, useEnclaveTokenStorage } from "wrappers";
import { getDriverDcapMrsignerAttestationSpecification, Store } from "./utils";

const ApiCoreWrapper = memo<React.PropsWithChildren>(({ children }) => {
  const { user, isLoading, getAccessTokenSilently } = useAuth0();
  const [store] = useState(new Store());
  const { state: storageEnclaveToken, setState: setStorageEnclaveToken } =
    useEnclaveTokenStorage();
  const sessionRef = useRef<SessionV2 | null>(null);
  const verificationKeyRef = useRef<Uint8Array | null>(null);
  const {
    diswaHost: host,
    diswaPort: port,
    diswaUseTls: useTls,
    unsafeDisableKnownRootCaCheck,
    clientLogRequests: logRequests,
    environment,
    insecureEnclavesEnabled,
  } = useConfiguration();
  const [client, setClient] = useState<Client | undefined>();
  const [sessionManager, setApiCoreSessionManager] = useState<
    SessionManager | undefined
  >();
  const [driverMrsignerAttestationSpec, driverMrsignerAttestationSpecHash] =
    useMemo(() => {
      const spec = getDriverDcapMrsignerAttestationSpecification(
        environment,
        insecureEnclavesEnabled
      );
      const md = forge.md.sha256.create();
      md.update(
        forge.util.binary.raw.encode(
          attestationProto.AttestationSpecification.encodeDelimited(
            spec
          ).finish()
        )
      );
      const hash = md.digest().toHex();
      return [spec, hash];
    }, [environment, insecureEnclavesEnabled]);
  useEffect(() => {
    const initializeClient = async () => {
      const email = user?.email;
      // TODO: use proper setup for enclave token
      if (!email) {
        return;
      }
      const platformAccessToken = await getAccessTokenSilently();
      if (storageEnclaveToken) {
        const enclaveToken = JSON.parse(storageEnclaveToken) as EnclaveToken;
        if (enclaveToken.email !== email) {
          logInfo(
            "Enclave token found belonging to different user, triggering enclave email MFA"
          );
          clearDecentriqStorage();
          setStorageEnclaveToken(null);
          return;
        }
      }
      const token = new UserToken(
        platformAccessToken,
        storageEnclaveToken
          ? (JSON.parse(storageEnclaveToken) as EnclaveToken).token
          : ""
      );
      const newClient = Client.create(email, token, {
        host,
        logRequests,
        port,
        unsafeDisableKnownRootCaCheck,
        useTls,
      });
      setClient(newClient);
    };
    void initializeClient();
  }, [
    host,
    port,
    useTls,
    unsafeDisableKnownRootCaCheck,
    user,
    getAccessTokenSilently,
    logRequests,
    storageEnclaveToken,
    setStorageEnclaveToken,
  ]);
  useEffect(() => {
    if (client) {
      setApiCoreSessionManager(
        new SessionManager(client, driverMrsignerAttestationSpec)
      );
    }
  }, [client, driverMrsignerAttestationSpec]);
  const reset = useCallback(() => {
    setClient(undefined);
    setApiCoreSessionManager(undefined);
    sessionRef.current = null;
    verificationKeyRef.current = null;
  }, [setClient, setApiCoreSessionManager]);
  useEffect(reset, [reset, user?.email]);
  const getSession = useCallback(async () => {
    if (!sessionManager) {
      throw new Error("Session manager is not initialized");
    }
    if (sessionRef.current !== null) {
      return sessionRef.current;
    }
    const session = await sessionManager.getV2();
    await session.setAuthToken({
      type: "enclave-access",
      value: (JSON.parse(storageEnclaveToken!) as EnclaveToken).token,
    });
    sessionRef.current = session;
    return session;
  }, [sessionManager, storageEnclaveToken, sessionRef]);
  const getVerificationKey = useCallback(
    async (session?: SessionV2) => {
      if (verificationKeyRef.current !== null) {
        return verificationKeyRef.current;
      }
      const effectiveSession = session ?? (await getSession());
      const key = await effectiveSession.getVerificationKey();
      verificationKeyRef.current = key;
      return key;
    },
    [getSession]
  );
  const contextValue = useMemo<ApiCoreContextValue>(
    () => ({
      client: client!,
      driverMrsignerAttestationSpecHash,
      getSession,
      getVerificationKey,
      reset,
      sessionManager: sessionManager!,
      store,
    }),
    [
      getVerificationKey,
      client,
      driverMrsignerAttestationSpecHash,
      getSession,
      reset,
      sessionManager,
      store,
    ]
  );
  if (!client || !sessionManager || isLoading) {
    return <Loading />;
  } else {
    return <ApiCoreProvider value={contextValue}>{children}</ApiCoreProvider>;
  }
});
ApiCoreWrapper.displayName = "ApiCoreWrapper";

export default ApiCoreWrapper;
